import { z } from 'zod'

export const FocusDaySchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine(value => {
    const date = new Date(`${value}T00:00:00Z`)
    return (
      Number.isFinite(date.getTime()) &&
      date.toISOString().slice(0, 10) === value
    )
  }, 'Choose a valid date')

export const FocusTimezoneSchema = z
  .string()
  .max(100)
  .refine(value => {
    try {
      new Intl.DateTimeFormat('en', { timeZone: value })
      return true
    } catch {
      return false
    }
  }, 'Choose a valid timezone')

export interface FocusScheduleBlock {
  id: string
  title: string
  kind: 'work' | 'break'
  startMinute: number
  endMinute: number
  reason?: string
  done: boolean
  workRef?: string
  timeEntryId?: string
  fromTime?: boolean
}

export const FocusScheduleBlockSchema = z
  .object({
    id: z.string().min(1).max(100),
    title: z.string().trim().min(1).max(240),
    kind: z.enum(['work', 'break']),
    startMinute: z.number().int().min(0).max(1439),
    endMinute: z.number().int().min(1).max(1440),
    reason: z.string().max(400).optional(),
    done: z.boolean().default(false),
    workRef: z.string().max(100).optional(),
    /** The logged time entry this block mirrors; logged time is the truth. */
    timeEntryId: z.string().max(100).optional(),
    /** Created by the time sync rather than planned; removed with its entry. */
    fromTime: z.boolean().optional(),
  })
  .refine(
    block => block.endMinute > block.startMinute,
    'End time must be after start time',
  ) as z.ZodType<FocusScheduleBlock, z.ZodTypeDef, unknown>

export interface FocusSchedulePreferences {
  timezone: string
  dayStart: number
  dayEnd: number
  includeCalendar: boolean
  excludedEventIds: string[]
  /** suggest = offer a Sync action; auto = planned blocks follow logged time. */
  timeSync: 'suggest' | 'auto'
}

export const FocusSchedulePreferencesSchema = z
  .object({
    timezone: FocusTimezoneSchema,
    dayStart: z.number().int().min(0).max(1439).default(540),
    dayEnd: z.number().int().min(1).max(1440).default(1020),
    includeCalendar: z.boolean().default(true),
    excludedEventIds: z.array(z.string().max(500)).max(500).default([]),
    timeSync: z.enum(['suggest', 'auto']).default('suggest'),
  })
  .refine(
    value => value.dayEnd > value.dayStart,
    'Workday must end after it starts',
  ) as z.ZodType<FocusSchedulePreferences, z.ZodTypeDef, unknown>

export interface FocusScheduleInput {
  /** A recovered AI draft is acknowledged atomically with its schedule save. */
  draftOperationId?: string
  date: string
  revision: string | null
  preferences: FocusSchedulePreferences
  blocks: FocusScheduleBlock[]
}

export const FocusScheduleInputSchema = z
  .object({
    draftOperationId: z.string().uuid().optional(),
    date: FocusDaySchema,
    revision: z.string().nullable(),
    preferences: FocusSchedulePreferencesSchema,
    blocks: z.array(FocusScheduleBlockSchema).max(100),
  })
  .superRefine((value, context) => {
    if (
      new Set(value.blocks.map(block => block.id)).size !== value.blocks.length
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Each block needs a unique ID',
        path: ['blocks'],
      })
    }
    // Manual plans may intentionally overlap. Benny reserves their combined
    // occupied time when suggesting additions; the timeline displays lanes.
  }) as z.ZodType<FocusScheduleInput, z.ZodTypeDef, unknown>

export interface FocusScheduleEvent {
  id: string
  title: string
  startMinute: number
  endMinute: number
  allDay: boolean
  busy: boolean
  source: string
}

export interface FocusScheduleWork {
  /** `<ModuleType>:<id>`; tasks and tickets are assigned work, anything can be recent. */
  ref: string
  title: string
  context: string
  priority: string | null
  /** Calendar day resolved by the server in the schedule timezone. */
  dueDate: string | null
  /** Module type behind the ref; derived from the ref when absent. */
  kind?: string
  /** assigned = on the user's plate; recent = they logged time on it lately. */
  source?: 'assigned' | 'recent'
  /** Minutes logged against it in the last two weeks. */
  recentMinutes?: number
  /** Minutes logged against it on the schedule's day. */
  loggedMinutes?: number
  /** Timer defaults taken from the latest entry on this item. */
  lastRoleId?: string | null
  lastBillable?: boolean
}

/** A time entry on the schedule's day, in the schedule's timezone. */
export interface FocusScheduleTimeEntry {
  id: string
  ref: string | null
  title: string
  context: string
  /** Null when the entry has a duration but no specific time. */
  startMinute: number | null
  endMinute: number | null
  minutes: number
  billable: boolean
  roleId: string | null
  description: string | null
}

export interface FocusScheduleResponse {
  pendingDraft?: FocusScheduleDraftReceipt | null
  date: string
  revision: string | null
  preferences: FocusSchedulePreferences
  blocks: FocusScheduleBlock[]
  calendar: FocusScheduleEvent[]
  calendarWarning: string | null
  work: FocusScheduleWork[]
  timeEntries: FocusScheduleTimeEntry[]
}

export interface FocusScheduleDraftReceipt {
  operationId: string
  status: 'running' | 'prepared' | 'review'
}

export interface FocusScheduleDraftResponse {
  operationId?: string
  input?: FocusScheduleInput
  blocks: FocusScheduleBlock[]
  summary: string
  unscheduled: string[]
}

/** An AI response only adds new blocks; it cannot restore deleted blocks or
 * overwrite edits made while generation was running. Clock-setting changes
 * require a fresh request because the generated minutes use the original zone. */
export function mergeFocusScheduleDraft(
  input: FocusScheduleInput,
  result: FocusScheduleDraftResponse,
  latest: FocusScheduleInput,
): FocusScheduleInput {
  if (
    input.date !== latest.date ||
    input.preferences.timezone !== latest.preferences.timezone ||
    input.preferences.dayStart !== latest.preferences.dayStart ||
    input.preferences.dayEnd !== latest.preferences.dayEnd
  )
    throw new Error(
      'Schedule settings changed while preparing suggestions. Review your current day before trying again.',
    )
  const known = new Set(
    [...input.blocks, ...latest.blocks].map(block => block.id),
  )
  const additions = result.blocks.filter(block => {
    if (known.has(block.id)) return false
    known.add(block.id)
    return true
  })
  if (latest.blocks.length + additions.length > 100)
    throw new Error(
      'Your newer edits leave too little room for these suggestions. Your current schedule is unchanged.',
    )
  return {
    ...latest,
    ...(result.operationId ? { draftOperationId: result.operationId } : {}),
    blocks: [...latest.blocks, ...additions].sort(
      (a, b) => a.startMinute - b.startMinute,
    ),
  }
}

/** Half-open intervals: back-to-back blocks are allowed. */
export function focusBlocksOverlap(
  a: { startMinute: number; endMinute: number },
  b: { startMinute: number; endMinute: number },
): boolean {
  return a.startMinute < b.endMinute && b.startMinute < a.endMinute
}

/** Find the first uninterrupted slot; merge overlapping commitments implicitly. */
export function findFocusSlot(
  start: number,
  end: number,
  duration: number,
  occupied: { startMinute: number; endMinute: number }[],
): number | null {
  let cursor = start
  for (const block of [...occupied].sort(
    (a, b) => a.startMinute - b.startMinute,
  )) {
    if (block.endMinute <= cursor) continue
    if (cursor + duration <= Math.min(block.startMinute, end)) return cursor
    cursor = Math.max(cursor, block.endMinute)
  }
  return cursor + duration <= end ? cursor : null
}

/** One day of the planner's week strip: how much was planned and finished. */
export interface FocusScheduleWeekDay {
  date: string
  planned: number
  done: number
}

export interface FocusScheduleWeekResponse {
  days: FocusScheduleWeekDay[]
}
